import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Book, Download, FileText, Palette, Loader2, LogIn, X, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';

interface PdfBookDownloadProps {
  storyId: number;
  storyTitle: string;
}

const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/[\s]+/g, ' ')
    .trim()
    .substring(0, 100)
    || 'story';
};

interface ProgressState {
  stage: string;
  message: string;
  percentage: number;
}

export function PdfBookDownload({ storyId, storyTitle }: PdfBookDownloadProps) {
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();
  const [downloadingType, setDownloadingType] = useState<'illustrated' | 'coloring' | null>(null);
  const [useProgressTracking, setUseProgressTracking] = useState(true);
  const [useAICover, setUseAICover] = useState(true);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  
  // Early return if storyId is invalid - use consistent validation
  if (!Number.isInteger(storyId) || storyId <= 0) {
    return (
      <Card className="border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Book className="w-5 h-5" />
            PDF Book Generator
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 text-center">
            <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">
              Invalid story selected
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
              Please navigate to a valid story page to generate books
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const cancelSSE = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setProgress(null);
    setDownloadingType(null);
    toast({
      title: "Generation cancelled",
      description: "Book generation has been cancelled.",
      duration: 3000,
    });
  };

  const handleSSEDownload = async (type: 'illustrated' | 'coloring') => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please log in to generate books",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Validate storyId before proceeding - use consistent validation
    if (!Number.isInteger(storyId) || storyId <= 0) {
      toast({
        title: "Invalid story",
        description: "Please select a valid story to generate a book",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setDownloadingType(type);
    setProgress({
      stage: 'initializing',
      message: 'Connecting to generation service...',
      percentage: 0,
    });

    try {
      const params = new URLSearchParams({
        type,
        generateCover: useAICover.toString(),
      });
      
      const eventSource = new EventSource(
        `/api/stories/${storyId}/books/stream?${params.toString()}`
      );
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('progress', (event) => {
        try {
          const data = JSON.parse(event.data);
          setProgress({
            stage: data.stage || 'generating',
            message: data.message || 'Processing...',
            percentage: data.percentage || 0,
          });
        } catch (error) {
          console.error('Error parsing progress event:', error);
        }
      });

      eventSource.addEventListener('complete', async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('SSE Complete event received:', data);
          
          // The downloadUrl is nested in the book object
          const downloadUrl = data.book?.downloadUrl || data.downloadUrl;
          
          if (!downloadUrl) {
            throw new Error('No download URL provided in SSE response');
          }
          
          // Fetch the PDF from the API endpoint with credentials
          const response = await fetch(downloadUrl, {
            method: 'GET',
            credentials: 'include',
          });
          
          if (!response.ok) {
            throw new Error(`Failed to download PDF: ${response.statusText}`);
          }
          
          // Get the PDF as a blob
          const blob = await response.blob();
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${sanitizeFilename(storyTitle)}-${type}-book.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          
          // Show success and cleanup
          toast({ title: "Book downloaded successfully!" });
          eventSource.close();
          eventSourceRef.current = null;
          setProgress(null);
          setDownloadingType(null);
        } catch (error) {
          console.error('Error handling complete event:', error);
          toast({
            title: "Download failed",
            description: error instanceof Error ? error.message : "Failed to download the book",
            variant: "destructive",
            duration: 5000,
          });
          eventSource.close();
          eventSourceRef.current = null;
          setProgress(null);
          setDownloadingType(null);
        }
      });

      eventSource.addEventListener('error', (event: Event) => {
        const messageEvent = event as MessageEvent;
        eventSource.close();
        eventSourceRef.current = null;

        let errorMessage = 'Failed to generate book';
        try {
          if (messageEvent.data) {
            const data = JSON.parse(messageEvent.data);
            errorMessage = data.error || errorMessage;
          }
        } catch {
          errorMessage = 'Connection error occurred';
        }

        toast({
          title: "Generation failed",
          description: errorMessage,
          variant: "destructive",
          duration: 5000,
        });

        setProgress(null);
        setDownloadingType(null);
      });

      eventSource.onerror = () => {
        if (eventSource.readyState === EventSource.CLOSED) {
          eventSource.close();
          eventSourceRef.current = null;
          setProgress(null);
          setDownloadingType(null);
          
          if (progress && progress.percentage < 100) {
            setUseProgressTracking(false);
            
            toast({
              title: "Connection lost",
              description: "Falling back to standard download...",
              duration: 3000,
            });
            
            handleDownload(type);
          }
        }
      };
    } catch (error) {
      console.error('SSE setup error:', error);
      toast({
        title: "SSE not supported",
        description: "Falling back to standard download...",
        duration: 3000,
      });
      handleDownload(type);
    }
  };

  const downloadBookMutation = useMutation({
    mutationFn: async (type: 'illustrated' | 'coloring') => {
      if (!isAuthenticated) {
        throw new Error('Please log in to generate books');
      }
      
      // Validate storyId before making request - use consistent validation
      if (!Number.isInteger(storyId) || storyId <= 0) {
        throw new Error('Invalid story ID - please select a valid story');
      }
      
      setDownloadingType(type);
      
      // Use direct fetch for binary PDF download instead of apiRequest
      const response = await fetch(`/api/stories/${storyId}/books`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Include credentials for authentication
        },
        credentials: 'include',
        body: JSON.stringify({ type })
      });
      
      if (!response.ok) {
        let errorMessage = 'Failed to generate book';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.error || errorMessage;
          }
        } catch {
          // Use default message
        }
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        throw new Error('Invalid response format. Expected PDF file.');
      }

      // Get binary data as ArrayBuffer first
      const arrayBuffer = await response.arrayBuffer();
      console.log(`Downloaded PDF: ${arrayBuffer.byteLength} bytes`);
      
      // Check if it's a valid PDF by looking at the header
      const uint8Array = new Uint8Array(arrayBuffer);
      const pdfHeader = String.fromCharCode.apply(null, Array.from(uint8Array.slice(0, 5)));
      console.log(`PDF Header: ${pdfHeader}`);
      
      if (!pdfHeader.startsWith('%PDF')) {
        console.error('Downloaded data does not appear to be a valid PDF');
        throw new Error('Invalid PDF data received - file appears to be corrupted');
      }
      
      // Convert ArrayBuffer to Blob with explicit PDF type
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFilename(storyTitle)}-${type}-book.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return type;
    },
    onSuccess: (type) => {
      toast({
        title: "Book downloaded!",
        description: `Your ${type} book has been saved to your downloads.`,
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      if (!error.message.includes('401') && !error.message.includes('Authentication')) {
        toast({
          title: "Generation failed",
          description: error.message,
          variant: "destructive",
          duration: 5000,
        });
      }
    },
    onSettled: () => {
      setDownloadingType(null);
    },
  });

  const handleDownload = (type: 'illustrated' | 'coloring') => {
    if (useProgressTracking) {
      handleSSEDownload(type);
    } else {
      downloadBookMutation.mutate(type);
    }
  };

  const getProgressBarColor = () => {
    if (!progress) return 'bg-blue-600';
    switch (progress.stage) {
      case 'initializing':
        return 'bg-blue-600';
      case 'generating':
      case 'processing':
        return 'bg-purple-600';
      case 'complete':
        return 'bg-green-600';
      default:
        return 'bg-blue-600';
    }
  };

  if (!isAuthenticated) {
    return (
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-700">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100" data-testid="pdf-books-title">
            <Book className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            PDF Book Generator
          </CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Generate beautifully formatted PDF books with AI-powered illustrations
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-8">
            <LogIn className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Authentication Required
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Please log in to generate and download PDF books
            </p>
            <Button 
              onClick={() => window.location.href = '/auth'}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Log In
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-700">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100" data-testid="pdf-books-title">
          <Book className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          PDF Book Generator
        </CardTitle>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Generate beautifully formatted PDF books with AI-powered illustrations
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <Label htmlFor="progress-tracking" className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                Real-time Progress Tracking
              </Label>
            </div>
            <Switch
              id="progress-tracking"
              checked={useProgressTracking}
              onCheckedChange={setUseProgressTracking}
              disabled={!!downloadingType}
              data-testid="toggle-progress-tracking"
            />
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 ml-6">
            {useProgressTracking 
              ? 'See real-time updates as your book is being generated'
              : 'Standard download without progress updates'}
          </p>
          
          <div className="flex items-center gap-2 ml-6">
            <Checkbox
              id="ai-cover"
              checked={useAICover}
              onCheckedChange={(checked) => setUseAICover(checked as boolean)}
              disabled={!!downloadingType}
              data-testid="checkbox-ai-cover"
            />
            <Label htmlFor="ai-cover" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              Generate AI cover (takes longer)
            </Label>
          </div>
        </div>

        {progress && (
          <div className="space-y-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 border border-blue-200 dark:border-blue-700 rounded-lg p-4" data-testid="sse-progress-display">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {progress.stage}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {Math.round(progress.percentage)}%
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {progress.message}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelSSE}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                data-testid="button-cancel-sse"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="relative">
              <Progress 
                value={progress.percentage} 
                className={`h-2 ${getProgressBarColor()}`}
                data-testid="progress-bar"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => handleDownload('illustrated')}
            disabled={downloadBookMutation.isPending || !!progress}
            className="h-auto p-4 flex flex-col items-start gap-2 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-950 border border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-download-illustrated"
          >
            <div className="flex items-center gap-2 w-full">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-900 dark:text-gray-100">Illustrated Book</span>
              {downloadingType === 'illustrated' && !progress ? (
                <Loader2 className="w-4 h-4 animate-spin ml-auto" />
              ) : (
                <Download className="w-4 h-4 text-gray-500 ml-auto" />
              )}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 text-left">
              Full story with illustrations and complete text
            </p>
          </button>

          <button
            onClick={() => handleDownload('coloring')}
            disabled={downloadBookMutation.isPending || !!progress}
            className="h-auto p-4 flex flex-col items-start gap-2 bg-white dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-950 border border-purple-200 dark:border-purple-700 hover:border-purple-300 dark:hover:border-purple-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-download-coloring"
          >
            <div className="flex items-center gap-2 w-full">
              <Palette className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900 dark:text-gray-100">Coloring Book</span>
              {downloadingType === 'coloring' && !progress ? (
                <Loader2 className="w-4 h-4 animate-spin ml-auto" />
              ) : (
                <Download className="w-4 h-4 text-gray-500 ml-auto" />
              )}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300 text-left">
              Simple text with outline drawings to color
            </p>
          </button>
        </div>
        
        {downloadingType && !progress && (
          <div className="text-center text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-950 rounded-lg p-3" data-testid="pdf-generation-progress">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>
                Generating your {downloadingType} book... This may take a moment.
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
